import React, { useMemo } from "react";
import { useEditorState, type Editor } from "@tiptap/react";
import IconButton from "../Buttons/IconButton/IconButton";
import {
  TOOLBAR_DIVIDER,
  isToolbarAction,
  toolbarData,
  type ToolbarAction,
  type ToolbarItem,
} from "./TextEditorToolbarConfig";

interface TextEditorToolbarProps {
  editor: Editor;
  items: ToolbarItem[];
  dataTestId: string;
}

const TextEditorToolbar: React.FC<TextEditorToolbarProps> = ({
  editor,
  items,
  dataTestId,
}) => {
  const actions = useMemo(() => items.filter(isToolbarAction), [items]);

  const activeActions = useEditorState({
    editor,
    selector: ({ editor: currentEditor }) =>
      actions.reduce<Partial<Record<ToolbarAction, boolean>>>((acc, action) => {
        acc[action] = toolbarData[action].isActive(currentEditor);
        return acc;
      }, {}),
  });

  const isDisabled = !editor.isEditable;

  return (
    <div className="text-editor-toolbar" data-testid={`${dataTestId}-toolbar`}>
      {items.map((item, index) => {
        if (item === TOOLBAR_DIVIDER) {
          return (
            <span
              key={`${item}-${index}-toolbar-item`}
              className="text-editor-toolbar-divider"
              data-testid={`${dataTestId}-toolbar-divider`}
            />
          );
        }

        const { icon, label, shortCutFriendly, run } = toolbarData[item];
        return (
          <IconButton
            key={`${item}-${index}-toolbar-item`}
            type="button"
            isIcon={icon}
            disabled={isDisabled}
            onClick={() => run(editor)}
            data-testid={`${dataTestId}-toolbar-${item}`}
            wrapperClassName={activeActions[item] ? "active" : ""}
            withTooltip={{
              content: shortCutFriendly
                ? `${label} (${shortCutFriendly})`
                : label,
            }}
          />
        );
      })}
    </div>
  );
};

export default TextEditorToolbar;
